import React, { useState, useEffect } from 'react';
import { Text, TextInput, Button } from 'mintel-react-ui';

import { changeLanguage } from '../shared/utils';
import useTranslation from '../translations';
import { StringField } from '../fields';
import { HiddenFields } from '../InputWrapper';
import PageWrapper from '../PageWrapper';
import styles from './ChangePassword.scss';

export type ChangePasswordProps = {
  token?: string | null;
  fields: {
    // eslint-disable-next-line camelcase
    old_password: StringField;
    password1: StringField;
    password2: StringField;
  };
  // eslint-disable-next-line camelcase
  urls: { user_preferences: string };
  errors: string[];
  info: string[];
  language?: string;
};

const ChangePassword = ({
  token,
  fields,
  urls,
  errors,
  info,
  language = 'en',
}: ChangePasswordProps): JSX.Element => {
  const { t } = useTranslation();
  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  const [oldPassword, setOldPassword] = useState(
    fields.old_password?.value || '',
  );
  const [password1, setPassword1] = useState(fields.password1?.value || '');
  const [password2, setPassword2] = useState(fields.password1?.value || '');

  return (
    <PageWrapper
      errors={errors}
      infos={info}
      successMessage="Your password was changed successfully."
    >
      <form method="POST" className={styles.changePassword}>
        <div>
          <Text.Heading2>
            {token ? t('Reset Password') : t('Change Password')}
          </Text.Heading2>
        </div>
        <div>
          <Text.Body2>
            {token
              ? t('Please confirm your new password in the fields below.')
              : t(
                  'If you would like to change your password, please enter your current and new password.',
                )}
          </Text.Body2>
        </div>
        {!token && (
          <TextInput
            id={fields.old_password?.id}
            name={fields.old_password?.name}
            showLabel
            showClear={false}
            label={t('Current password')}
            value={oldPassword}
            password
            placeholder={t('Enter current password')}
            showIcon={false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOldPassword(e.target.value);
            }}
          />
        )}
        <TextInput
          id={fields.password1?.id}
          name={fields.password1?.name}
          showLabel
          showClear={false}
          label={t('New password')}
          value={password1}
          password
          placeholder={t('Enter your new password')}
          showIcon={false}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPassword1(e.target.value);
          }}
        />
        <TextInput
          id={fields.password2?.id}
          name={fields.password2?.name}
          showLabel
          showClear={false}
          label={t('Confirm new password')}
          value={password2}
          password
          placeholder={t('Confirm your new password')}
          showIcon={false}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPassword2(e.target.value);
          }}
        />
        <div className={styles.buttonRow}>
          <div className={styles.button}>
            <Button.CancelButton
              fullWidth
              handleClick={() => {
                window.location.pathname = urls.user_preferences;
              }}
            >
              {t('Cancel')}
            </Button.CancelButton>
          </div>
          <div className={styles.button}>
            <Button.ActionButton
              type="submit"
              disabled={
                !password1.length ||
                !password2.length ||
                (!token && !oldPassword)
              }
              fullWidth
              handleClick={() => {
                // Handled by the form
              }}
              status={Button.Status.DEFAULT}
            >
              {token ? t('Reset password') : t('Submit password')}
            </Button.ActionButton>
          </div>
        </div>
        <HiddenFields fields={fields} />
      </form>
    </PageWrapper>
  );
};

export default ChangePassword;
